import React, { useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
} from 'react-native';
import { ImageCarousel } from '@/components/residents/bulletins/ImageCarousel';
import { PADDING } from '@/constants/residents/homeConstants';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { cleanDescription, formatRelativeDate } from '@/utils/residents/utils';
import { StackNavigationProp } from '@react-navigation/stack';
import { RouteProp } from '@react-navigation/native';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { DraggableModal } from '@/components/common/residents/DraggableModal';
import { useScrollableContent } from '@/hooks/residents/useScrollableContent';

type BulletinDetailScreenNavigationProp = StackNavigationProp<RootStackParamList, 'BulletinDetail'>;
type BulletinDetailScreenRouteProp = RouteProp<RootStackParamList, 'BulletinDetail'>;

interface BulletinDetailScreenProps {
  route: BulletinDetailScreenRouteProp;
  navigation: BulletinDetailScreenNavigationProp;
}

export const BulletinDetailScreen: React.FC<BulletinDetailScreenProps> = ({ 
  route, 
  navigation 
}) => {
  const styles = useThemedStyles(makeStyles);
  const { bulletin } = route.params;
  const scrollViewRef = useRef<ScrollView>(null);
  const hasImages = bulletin.files && bulletin.files.length > 0;
  
  // Use the actual API field 'description' (iOS maps this to responseDescription)
  const descriptionText = bulletin.description || bulletin.responseDescription || '';

  const handleClose = () => {
    navigation.goBack();
  };

  // Use the reusable hook for scrollable content logic
  const {
    canDrag,
    canScroll,
    handleContentSizeChange,
    handleLayout,
  } = useScrollableContent();

  return (
    <DraggableModal 
      onClose={handleClose} 
      canDrag={canDrag}
      testID="bulletin-detail-modal"
    >
      {/* Always render ScrollView to get proper measurements */}
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={[
          styles.scrollContent,
          !hasImages && styles.scrollContentWithPadding
        ]}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}
        bounces={true}
        onContentSizeChange={handleContentSizeChange}
        onLayout={handleLayout}
        scrollEnabled={canScroll}
        testID="bulletin-detail-scrollview"
      >
        {/* Image Carousel */}
        {hasImages && (
          <ImageCarousel files={bulletin.files!} />
        )}
        
        {/* Content Stack */}
        <View style={styles.contentStack}>
          <Text style={styles.dateLabel}>
            {formatRelativeDate(bulletin.updatedAt)}
          </Text>
          
          <Text style={styles.titleLabel}>
            {bulletin.title}
          </Text>
          
          <Text style={styles.descriptionLabel}>
            {cleanDescription(descriptionText)}
          </Text>
        </View>
      </ScrollView>
    </DraggableModal>
  );
};

const makeStyles = ({ colors }: ThemeContextValue) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: PADDING.padding6,
  },
  scrollContentWithPadding: {
    paddingTop: PADDING.padding10, // iOS: top inset when no images
  },
  contentStack: {
    padding: PADDING.padding4,
    gap: PADDING.padding2, // iOS spacing: .Padding.padding2
  },
  dateLabel: {
    fontSize: 12,
    fontWeight: '400',
    color: colors.text.muted, // iOS .Text.date
    lineHeight: 16,
  },
  titleLabel: {
    fontSize: 20,
    fontWeight: '600', // iOS .Notifications.title (larger for detail)
    color: colors.text.primary,
    lineHeight: 26,
  },
  descriptionLabel: {
    fontSize: 16,
    fontWeight: '400', // iOS .Notifications.description (larger for detail)
    color: colors.text.primary,
    lineHeight: 22,
  },
});